import { LANGUAGE } from "@stores/SettingsStore";
import getLanguageByCode from "@src/utils/getLanguageByCode";
import { authClient } from "@services/authService";
import { api } from "@services/api";

// Localizaion contains localization preferences such as language, unit of
// measurement, date format etc.
export type Localization = {
  // Language preferred by the user.
  language: LANGUAGE;
};

// UI contains UI preferences
export type UI = {
  // Should left bar be expanded?
  left_bar_expanded: boolean;
};

// Area units
export enum AreaUnit {
  // Hectare (ha)
  Hectare = "ha",
  // Acre (ac)
  Acre = "ac",
}

// Length units. Not persisted in Preferences — derived from the area unit
// (acre => feet, hectare => metres) via SettingsStore.lengthUnit.
export enum LengthUnit {
  // Metre (m)
  Meter = "m",
  // Foot (ft)
  Foot = "ft",
}

// Weight units
export enum WeightUnit {
  // Ton (metric, 1000 kg)
  Ton = "t",
  // Centner / quintal (100 kg)
  Centner = "dz",
  // Kilogram (kg)
  Kilogram = "kg",
  // Pound (lb)
  Pound = "lb",
  // US short ton (2000 lb)
  ShortTon = "ton",
  // Bushel (bu)
  Bushel = "bu",
  // Bag (variable weight)
  Bag = "bag",
}

// Preferences contains all of the user preferences.
export type Preferences = {
  // Localization preferences
  localization: Localization;
  // UI preferences
  ui: UI;

  units: {
    weight: WeightUnit;
    area: AreaUnit;
    poundsPerBushel: number;
    poundsPerBag: number;
    // Crop whose standard bushel weight poundsPerBushel came from. Display-only
    // (the settings picker re-selects it); conversions never read it. Absent on
    // preference rows written before 2026-09-11.
    bushelCropId?: string;
  };

  // Time when preferences has been updated for the last time.
  updated_at?: number;
};

// Key for the local storage where we store user preferences.
const LS_PREFERENCES_KEY = "gg_prefs";

// Preferences (units, language, UI) are per logged-in PERSON — they are NOT
// tied to the account being viewed. So a team member who switches into an
// owner's account keeps their OWN units everywhere, and never changes the
// owner's units. The server copy (/api/v1/me/preferences) is the source of
// truth since 2026-09; localStorage is the pre-sign-in copy and the fallback.
//
// Two local keys: per-user (`gg_prefs:<id>`) so two people sharing a browser
// don't swap units, and the bare key, which holds whatever was saved LAST and
// is what the login page reads before anyone is signed in.
function prefsKey(userId?: string): string {
  return userId ? `${LS_PREFERENCES_KEY}:${userId}` : LS_PREFERENCES_KEY;
}

// Server copies and old local blobs may predate a field; fill the gaps from
// defaults rather than letting `undefined` reach a unit label ("84 undefined").
function withDefaults(p: Partial<Preferences> | null | undefined): Preferences {
  const d = defaultPreferences();
  return {
    localization: { ...d.localization, ...(p?.localization ?? {}) },
    ui: { ...d.ui, ...(p?.ui ?? {}) },
    units: { ...d.units, ...(p?.units ?? {}) },
    updated_at: p?.updated_at ?? d.updated_at,
  };
}

// defaultPreferences return default preferences.
function defaultPreferences(): Preferences {
  return {
    localization: {
      language: getLanguageByCode(navigator.language),
    },
    ui: {
      left_bar_expanded: true,
    },
    units: {
      area: AreaUnit.Hectare,
      weight: WeightUnit.Ton,
      poundsPerBag: 100,
      poundsPerBushel: 60,
    },
    updated_at: 0,
  };
}

// loadFromLocalOrDefault loads preferences from the local storage or returns default
// preferences if local storage does not contain desired key.
export function loadFromLocalOrDefault(userId?: string): Preferences {
  // Per-user key first, then fall back to the legacy global key (one-time
  // migration for users who already had preferences saved), then defaults.
  const stored =
    (userId ? localStorage.getItem(prefsKey(userId)) : null) ??
    localStorage.getItem(LS_PREFERENCES_KEY);
  if (stored) {
    try {
      return withDefaults(JSON.parse(stored) as Partial<Preferences>);
    } catch { }
  }
  return defaultPreferences();
}

function hasLocal(userId?: string): boolean {
  try {
    return Boolean(
      (userId ? localStorage.getItem(prefsKey(userId)) : null) ??
        localStorage.getItem(LS_PREFERENCES_KEY)
    );
  } catch {
    return false;
  }
}

export type PreferencesSource = "server" | "local" | "default";

/**
 * The signed-in user's preferences. `source` tells the caller whether the
 * server already has a copy — when it does not, the store writes one back so
 * the next browser/device starts from the same place.
 */
export async function preferences(
  userId?: string
): Promise<{ prefs: Preferences; source: PreferencesSource }> {
  // silent: a failure here is handled by the store (local fallback), and a
  // toast on every page load would be worse than the missing preference.
  const res = await api.get<{ preferences: Partial<Preferences> | null }>(
    "/api/v1/me/preferences",
    undefined,
    { silent: true }
  );
  if (res.preferences) return { prefs: withDefaults(res.preferences), source: "server" };
  return {
    prefs: loadFromLocalOrDefault(userId),
    source: hasLocal(userId) ? "local" : "default",
  };
}

/**
 * Persist preferences: always to localStorage (both keys — see prefsKey), and
 * to the server when signed in. The local write happens first so the choice
 * survives even if the request fails.
 */
export async function updatePreferences(
  preferences: Preferences,
  userId?: string
): Promise<void> {
  const json = JSON.stringify(preferences);
  try {
    localStorage.setItem(LS_PREFERENCES_KEY, json);
    if (userId) localStorage.setItem(prefsKey(userId), json);
  } catch {
    /* storage disabled or full — the server copy still goes through */
  }
  if (userId) {
    await api.put("/api/v1/me/preferences", preferences, { silent: true });
  }
}

export type Features = {
  // Is prescription maps available?
  prescription_maps: boolean;
};

export function defaultUserFeatures(): Features {
  return {
    prescription_maps: true,
  };
}

export async function userFeatures(): Promise<Features> {
  return defaultUserFeatures();
}

// userConfigs returns the feature-flag keys enabled for the logged-in user
// (admin-managed "configs"). Gate a feature with settingsStore.hasConfig(key).
export async function userConfigs(): Promise<string[]> {
  const res = await api.get<{ keys: string[] }>("/api/v1/configs/me");
  return res.keys ?? [];
}

// Profile information
export type Profile = {
  // User email address
  email: string;
  // Full user name
  full_name: string;
  // User phone number
  phone_number: string;
  // Organization name
  organization_name: string;
};

export type Iam = {
  // Current user id
  id: string;
  // Current user profile
  user: Profile;
  // Actor user id
  actor_id: string;
  // Actor profile
  actor: Profile;
};

export async function updateProfile(
  full_name?: string,
  phone_number?: string,
  organization_name?: string
): Promise<Profile> {
  // better-auth's updateUser persists the name and the two profile
  // additionalFields (backend lib/auth.ts). An empty box stores "" (cleared).
  const { error } = await authClient.updateUser({
    name: full_name ?? "",
    phoneNumber: phone_number?.trim() ?? "",
    organizationName: organization_name?.trim() ?? "",
  } as { name: string });
  if (error) {
    throw new Error(error.message ?? "Failed to update profile");
  }

  return {
    email: "",
    full_name: full_name ?? "",
    phone_number: phone_number ?? "",
    organization_name: organization_name ?? "",
  };
}

export type Account = {
  // User id
  id: string;
  // Email address
  email: string;
  // User name
  name: string | null;
  // Whether the user has a paid subscription (gates NDVI history + prescription maps)
  subscriptionPaid: boolean;
  // Whether a potato camera is installed (gates camera layers + histograms)
  cameraInstalled: boolean;
  // Whether the address is confirmed. Login already refuses unverified accounts,
  // so this is true in practice — but that is an invariant of the login path,
  // not something a feature that emails the user should assume for itself.
  emailVerified: boolean;
  // Profile contact details (better-auth additionalFields). Absent while
  // impersonating — /api/v1/me does not carry them.
  phoneNumber?: string | null;
  organizationName?: string | null;
};
